import React, { useEffect, useState } from 'react';
import { Dialog, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme } from '@mui/material';
import { MdClose } from 'react-icons/md';
import useAdminsApi from '../../api/useAdminsApi';
import CommonLoader from '../loaders/CommonLoader';

const AdminWiseDetailsModal = ({ open, onClose, filter }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { getAdminWiseCalculatedInfo, loading } = useAdminsApi();
    const [adminData, setAdminData] = useState(null);
    const [allStatuses, setAllStatuses] = useState([]);
    const [allAdmins, setAllAdmins] = useState([]);
    const [isSalesMode, setIsSalesMode] = useState(false);

    // ---- Time Filter ----
    const [timeFilter, setTimeFilter] = useState('daily');
    const [start_date, setStartDate] = useState('');
    const [end_date, setEndDate] = useState('');

    useEffect(() => {
        if (open && filter) {
            // Don't call API for custom filter without both dates
            if (timeFilter === 'custom' && (!start_date || !end_date)) return;
            setIsSalesMode(filter === 'sales' || filter === 'users');
            fetchData();
        }
    }, [open, filter, timeFilter, ...(timeFilter === 'custom' ? [start_date, end_date] : [])]);

    const handleTimeFilterChange = (e) => {
        setTimeFilter(e.target.value);
    };

    const handleDateChange = (e, dateType) => {
        const value = e.target.value;
        if (dateType === 'start') {
            setStartDate(value);
            if (end_date && value > end_date) setEndDate(value);
        } else {
            if (!start_date || value >= start_date) setEndDate(value);
        }
    };

    const fetchData = async () => {
        try {
            const sd = timeFilter === 'custom' ? start_date : '';
            const ed = timeFilter === 'custom' ? end_date : '';
            const res = await getAdminWiseCalculatedInfo(filter, timeFilter, sd, ed);
            const payload = res?.data?.payload;
            
            if (payload) {
                if (filter === 'sales' || filter === 'users') {
                    // Sales/Users mode: payload contains admin details directly
                    // Structure: { username: { total_counts, ... }, ... }
                    const adminNames = Object.keys(payload);
                    setAllAdmins(adminNames);
                    setAllStatuses([]); // Not used in sales/users mode
                    setAdminData(payload);
                } else {
                    // Status-wise mode: payload contains status keys
                    const statuses = Object.keys(payload);
                    setAllStatuses(statuses);

                    // Extract all unique admin names dynamically
                    const adminNames = new Set();
                    Object.values(payload).forEach(statusData => {
                        Object.keys(statusData).forEach(key => {
                            if (key !== 'total_counts') {
                                adminNames.add(key);
                            }
                        });
                    });
                    setAllAdmins(Array.from(adminNames));
                    setAdminData(payload);
                }
            }
        } catch (error) {
            console.error('Error fetching admin wise data:', error);
        }
    };

    const getStatusColor = (status) => {
        const colorMap = {
            'pending': 'warning',
            'accepted': 'info',
            'scheduled': 'primary',
            'completed': 'success',
            'deleted': 'danger',
            'cancelled': 'danger',
            'rejected': 'danger'
        };
        return colorMap[status.toLowerCase()] || 'secondary';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ 
                sx: { 
                    borderRadius: '16px',
                    backgroundColor: isDark ? '#1a1a2e' : '#ffffff',
                    color: isDark ? '#e5e7eb' : '#1f2937'
                } 
            }}
        >
            <div className={`card mb-0 ${isDark ? 'bg-dark-mode border-dark' : 'bg-white'}`}>
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3"
                     style={{ 
                         backgroundColor: isDark ? '#16213e' : '#f9fafb',
                         borderBottom: `1px solid ${isDark ? '#2d3748' : '#e5e7eb'}`
                     }}
                >
                    <h6 className="card-title mb-0" style={{ color: isDark ? '#f3f4f6' : '#111827' }}>
                        {isSalesMode ? `${filter === 'users' ? 'Users' : 'Sales'} Admin Wise Details` : `Admin Wise Details - ${filter?.charAt(0).toUpperCase() + filter?.slice(1)}`}
                    </h6>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        <select
                            className='form-select form-select-sm w-auto bg-base border text-secondary-light'
                            value={timeFilter}
                            onChange={handleTimeFilterChange}
                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                            <option value="custom">Custom</option>
                        </select>
                        {timeFilter === 'custom' && (
                            <>
                                <input
                                    type="date"
                                    className="form-control form-control-sm w-auto"
                                    value={start_date}
                                    onChange={(e) => handleDateChange(e, 'start')}
                                    max={end_date || undefined}
                                />
                                <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>to</span>
                                <input
                                    type="date"
                                    className="form-control form-control-sm w-auto"
                                    value={end_date}
                                    onChange={(e) => handleDateChange(e, 'end')}
                                    min={start_date || undefined}
                                />
                            </>
                        )}
                        <button
                            type="button"
                            className={`btn btn-sm p-1 d-flex justify-content-center align-items-center ${
                                isDark ? 'btn-dark text-light' : 'btn-light'
                            }`}
                            onClick={onClose}
                            style={{
                                backgroundColor: isDark ? '#2d3748' : '#f3f4f6',
                                color: isDark ? '#e5e7eb' : '#374151',
                                border: `1px solid ${isDark ? '#4a5568' : '#d1d5db'}`
                            }}
                        >
                            <MdClose size={18} />
                        </button>
                    </div>
                </div>

                <div className="card-body" style={{ backgroundColor: isDark ? '#1a1a2e' : '#ffffff' }}>
                    {loading ? (
                        <div className="text-center py-5">
                            <CommonLoader />
                        </div>
                    ) : adminData ? (
                        <div className="table-responsive">
                            {isSalesMode ? (
                                // Sales/Users Mode: Show admin details with total counts
                                <TableContainer component={Paper} sx={{ 
                                    boxShadow: 'none', 
                                    border: `1px solid ${isDark ? '#2d3748' : '#e5e7eb'}`,
                                    backgroundColor: isDark ? '#0f1419' : '#ffffff'
                                }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ 
                                                backgroundColor: isDark ? '#16213e' : '#f9fafb'
                                            }}>
                                                <TableCell 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        backgroundColor: isDark ? '#16213e' : '#f9fafb',
                                                        color: isDark ? '#f3f4f6' : '#111827'
                                                    }}
                                                >
                                                    Admin
                                                </TableCell>
                                                <TableCell 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        textAlign: 'center',
                                                        backgroundColor: isDark ? '#16213e' : '#f9fafb',
                                                        color: isDark ? '#f3f4f6' : '#111827'
                                                    }}
                                                >
                                                    {filter === 'users' ? 'Total Users' : 'Total Sales'}
                                                </TableCell>
                                                {filter === 'sales' && (
                                                    <TableCell 
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            textAlign: 'center',
                                                            backgroundColor: isDark ? '#16213e' : '#f9fafb',
                                                            color: isDark ? '#10b981' : '#059669'
                                                        }}
                                                    >
                                                        Total Amount
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {allAdmins.map((admin, index) => {
                                                const adminInfo = adminData[admin] || {};
                                                return (
                                                    <TableRow 
                                                        key={admin}
                                                        sx={{ 
                                                            '&:last-child td, &:last-child th': { border: 0 },
                                                            backgroundColor: isDark 
                                                                ? (index % 2 === 0 ? '#0f1419' : '#1a1f2e')
                                                                : (index % 2 === 0 ? '#ffffff' : '#f9fafb'),
                                                            '&:hover': {
                                                                backgroundColor: isDark ? '#252b3d' : '#f3f4f6'
                                                            }
                                                        }}
                                                    >
                                                        <TableCell 
                                                            component="th" 
                                                            scope="row"
                                                            sx={{ 
                                                                textTransform: 'capitalize',
                                                                fontWeight: '600',
                                                                color: isDark ? '#e5e7eb' : '#374151'
                                                            }}
                                                        >
                                                            {admin}
                                                        </TableCell>
                                                        <TableCell 
                                                            sx={{ 
                                                                textAlign: 'center',
                                                                fontWeight: '500',
                                                                color: isDark ? '#e5e7eb' : '#374151'
                                                            }}
                                                        >
                                                            {adminInfo.total_counts || adminInfo.count || 0}
                                                        </TableCell>
                                                        {filter === 'sales' && (
                                                            <TableCell 
                                                                sx={{ 
                                                                    textAlign: 'center',
                                                                    fontWeight: 'bold',
                                                                    color: isDark ? '#10b981' : '#059669',
                                                                    fontSize: '1.1rem'
                                                                }}
                                                            >
                                                                ₹ {adminInfo.total_amount || adminInfo.amount || 0}
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                // Status-wise Mode: Original table structure
                                <TableContainer component={Paper} sx={{ 
                                    boxShadow: 'none', 
                                    border: `1px solid ${isDark ? '#2d3748' : '#e5e7eb'}`,
                                    backgroundColor: isDark ? '#0f1419' : '#ffffff'
                                }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ 
                                                backgroundColor: isDark ? '#16213e' : '#f9fafb'
                                            }}>
                                                <TableCell 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        backgroundColor: isDark ? '#16213e' : '#f9fafb',
                                                        borderRight: `1px solid ${isDark ? '#2d3748' : '#e5e7eb'}`,
                                                        color: isDark ? '#f3f4f6' : '#111827'
                                                    }}
                                                >
                                                    Status
                                                </TableCell>
                                                {allAdmins.map(admin => (
                                                    <TableCell 
                                                        key={admin}
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            textAlign: 'center',
                                                            backgroundColor: isDark ? '#16213e' : '#f9fafb',
                                                            textTransform: 'capitalize',
                                                            color: isDark ? '#f3f4f6' : '#111827'
                                                        }}
                                                    >
                                                        {admin}
                                                    </TableCell>
                                                ))}
                                                <TableCell 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        textAlign: 'center',
                                                        backgroundColor: isDark ? '#16213e' : '#f9fafb',
                                                        color: isDark ? '#10b981' : '#059669'
                                                    }}
                                                >
                                                    Total
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {allStatuses.map((status, index) => (
                                                <TableRow 
                                                    key={status}
                                                    sx={{ 
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        backgroundColor: isDark 
                                                            ? (index % 2 === 0 ? '#0f1419' : '#1a1f2e')
                                                            : (index % 2 === 0 ? '#ffffff' : '#f9fafb'),
                                                        '&:hover': {
                                                            backgroundColor: isDark ? '#252b3d' : '#f3f4f6'
                                                        }
                                                    }}
                                                >
                                                    <TableCell 
                                                        component="th" 
                                                        scope="row"
                                                        sx={{ 
                                                            borderRight: `1px solid ${isDark ? '#2d3748' : '#e5e7eb'}`,
                                                            textTransform: 'capitalize',
                                                            fontWeight: '600',
                                                            color: isDark ? '#e5e7eb' : '#374151'
                                                        }}
                                                    >
                                                        <span className={`badge bg-${getStatusColor(status)} bg-opacity-10 text-${getStatusColor(status)}-600 border border-${getStatusColor(status)}-main px-12 py-6 radius-4 fw-medium`}>
                                                            {status}
                                                        </span>
                                                    </TableCell>
                                                    {allAdmins.map(admin => (
                                                        <TableCell 
                                                            key={admin}
                                                            sx={{ 
                                                                textAlign: 'center',
                                                                fontWeight: '500',
                                                                color: isDark ? '#e5e7eb' : '#374151'
                                                            }}
                                                        >
                                                            {adminData[status]?.[admin] || 0}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell 
                                                        sx={{ 
                                                            textAlign: 'center',
                                                            fontWeight: 'bold',
                                                            color: isDark ? '#10b981' : '#059669',
                                                            fontSize: '1.1rem'
                                                        }}
                                                    >
                                                        {adminData[status]?.total_counts || 0}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-5" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                            No data available
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default AdminWiseDetailsModal;
